/**
 * @file ReceiptLabelsBlock.tsx
 * @description Блок «Этикетки» проведённой приёмки в зоне полос: «5 из 8
 *   поз. · ⚠ 2 устарели · 1 не печаталась», [Печать этикеток],
 *   «История печати (N) ›» (phase19.22, W17-5a, W17-9c; ui-design §1).
 *
 * Главная кнопка документа остаётся «Разместить» (решение пользователя
 * 2026-09-25 п.2) — у этикеток своё видимое место над позициями. Всё
 * напечатано — кнопка становится второстепенной «Напечатать ещё». На 360 —
 * столбиком, на `≥lg` — полосой: текст слева, кнопки справа.
 *
 * Ссылка на историю зависит от ширины (замечание пользователя 2026-09-26):
 * на `≥lg` история уже стоит секцией в подвале (`ReceiptFooter`), и ссылка
 * «К истории печати ↓» прокручивает к ней; на `<lg` секции нет — «История
 * печати (N) ›» открывает шторку. Подпись меняется классами (без мигания),
 * поведение решается в момент клика по `matchMedia`; секции нет в DOM
 * (журнал ещё не загрузился) — тоже шторка.
 */

import { ArrowDown, ChevronRight, Printer, Tags } from "lucide-react";
import type { MouseEvent } from "react";

import { LABEL_PRINT_LOG_ANCHOR, LABEL_TEXT } from "./label-texts";
import { Button, buttonVariants } from "../../ui/button";
import { cn } from "@/lib/utils";
import type { ReceiptLabelStatusDto } from "../../../lib/api-labels";
import { pluralRu } from "../../../lib/plural";

interface Props {
  status: ReceiptLabelStatusDto;
  onPrint: () => void;
  onOpenHistory: () => void;
}

/** Брейкпоинт `lg` Tailwind 4 (64rem) — с него история печати стоит в подвале. */
const LG_QUERY = "(min-width: 64rem)";

/** ≥lg и секция истории на странице — прокрутить к ней; иначе — шторка. */
function goToHistory(event: MouseEvent<HTMLAnchorElement>, onOpenHistory: () => void): void {
  event.preventDefault();
  const wide = typeof window.matchMedia === "function" && window.matchMedia(LG_QUERY).matches;
  const target = wide ? document.getElementById(LABEL_PRINT_LOG_ANCHOR) : null;
  if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  else onOpenHistory();
}

/** «⚠ 2 устарели · 1 не печаталась» — только ненулевые части. */
function problemsText(status: ReceiptLabelStatusDto): string | null {
  const parts: string[] = [];
  if (status.staleItems > 0) parts.push(`${status.staleItems} ${status.staleItems === 1 ? "устарела" : "устарели"}`);
  if (status.notPrintedItems > 0) {
    parts.push(`${status.notPrintedItems} ${status.notPrintedItems === 1 ? "не печаталась" : "не печатались"}`);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

export function ReceiptLabelsBlock({ status, onPrint, onOpenHistory }: Props) {
  const problems = problemsText(status);
  const allDone = !problems && status.totalItems > 0;
  const positions = pluralRu(status.totalItems, ["позицию", "позиции", "позиций"]);

  return (
    <section
      aria-label="Этикетки"
      className="flex flex-col gap-3 rounded-xl bg-surface-lowest p-4 ring-ghost lg:flex-row lg:items-center lg:justify-between"
    >
      <div className="flex min-w-0 items-start gap-3">
        <Tags className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-medium">
            {allDone
              ? `Этикетки напечатаны на все ${status.totalItems} ${positions}`
              : `Этикетки: ${status.printedItems} из ${status.totalItems} поз.`}
          </span>
          {problems ? <span className="text-sm text-warning">⚠ {problems}</span> : null}
        </div>
      </div>
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
        <a
          href={`#${LABEL_PRINT_LOG_ANCHOR}`}
          className={cn(buttonVariants({ variant: "ghost" }), "min-h-12 justify-start lg:order-1")}
          onClick={(e) => goToHistory(e, onOpenHistory)}
        >
          <span className="inline-flex items-center gap-1 lg:hidden">
            {LABEL_TEXT.historyTitle} ({status.jobsCount})
            <ChevronRight className="size-4" aria-hidden />
          </span>
          <span className="hidden items-center gap-1 lg:inline-flex">
            {LABEL_TEXT.historyScroll}
            <ArrowDown className="size-4" aria-hidden />
          </span>
        </a>
        <Button
          variant={allDone ? "outline" : "default"}
          className="min-h-14 w-full text-base lg:order-2 lg:w-auto"
          onClick={onPrint}
        >
          <Printer className="size-5" />
          <span className="ml-2">{allDone ? LABEL_TEXT.printMore : LABEL_TEXT.dialogTitle}</span>
        </Button>
      </div>
    </section>
  );
}
